/**
 * Validation Utilities for Universal Export
 *
 * VALIDATION RULES:
 * 1. At least one column must be selected
 * 2. If no rows selected, must have at least one filter OR return all
 * 3. Warn at 10,000 rows, hard limit at 100,000 rows
 * 4. Filter values must be valid for their operators
 */

import type {
  ExportOption,
  ValidationError,
} from '@/components/export/universal/types';

/**
 * Export size limits
 */
export const EXPORT_LIMITS = {
  WARNING_THRESHOLD: 10_000,
  HARD_LIMIT: 100_000,
} as const;

/**
 * Validate column selection
 *
 * @param columns - Selected columns
 * @returns Validation errors or null if valid
 */
export function validateColumns(
  columns: readonly ExportOption[]
): readonly ValidationError[] | null {
  if (columns.length === 0) {
    return [
      {
        field: 'columns',
        message: 'Please select at least one column to export.',
        details: { selectedCount: 0 },
      },
    ];
  }

  return null;
}

/**
 * Validate filter selection
 *
 * @param filters - Map of filter section IDs to selected options
 * @param selectedRows - Selected row keys
 * @param requireFiltersWhenNoSelection - Whether filters are required when no rows selected
 * @returns Validation errors or null if valid
 */
export function validateFilters(
  filters: Readonly<Record<string, readonly ExportOption[]>>,
  selectedRows: readonly string[],
  requireFiltersWhenNoSelection = true
): readonly ValidationError[] | null {
  // If rows are selected, no filter validation needed
  if (selectedRows.length > 0) {
    return null;
  }

  // If no rows selected and filters not required, return valid
  if (!requireFiltersWhenNoSelection) {
    return null;
  }

  // Check if any filters are selected
  const hasFilters = Object.values(filters).some(
    filterOptions => filterOptions.length > 0
  );

  if (!hasFilters) {
    return [
      {
        field: 'filters',
        message:
          'Please select at least one filter, or select specific rows to export.',
        details: { filterSections: Object.keys(filters), selectedCount: 0 },
      },
    ];
  }

  return null;
}

/**
 * Validate row count
 *
 * @param rowCount - Number of rows to export
 * @returns Validation errors or null if valid
 */
export function validateRowCount(
  rowCount: number
): readonly ValidationError[] | null {
  const errors: ValidationError[] = [];

  if (rowCount > EXPORT_LIMITS.HARD_LIMIT) {
    errors.push({
      field: 'rows',
      message: `Export exceeds maximum limit of ${EXPORT_LIMITS.HARD_LIMIT.toLocaleString()} rows. Please refine your filters or select fewer rows.`,
      details: { rowCount, limit: EXPORT_LIMITS.HARD_LIMIT },
    });
  } else if (rowCount > EXPORT_LIMITS.WARNING_THRESHOLD) {
    errors.push({
      field: 'rows',
      message: `Warning: Exporting ${rowCount.toLocaleString()} rows. This may take several minutes.`,
      details: { rowCount, threshold: EXPORT_LIMITS.WARNING_THRESHOLD },
    });
  }

  return errors.length > 0 ? errors : null;
}

/**
 * Validate export configuration
 *
 * Combines all validation rules into a single function.
 *
 * @param filters - Selected filters
 * @param columns - Selected columns
 * @param selectedRows - Selected row keys
 * @param options - Validation options
 * @returns Validation errors or null if valid
 */
export function validateExport(
  filters: Readonly<Record<string, readonly ExportOption[]>>,
  columns: readonly ExportOption[],
  selectedRows: readonly string[],
  options?: {
    readonly requireFiltersWhenNoSelection?: boolean;
    readonly estimatedRowCount?: number;
  }
): readonly ValidationError[] | null {
  const errors: ValidationError[] = [];

  // Validate columns
  const columnErrors = validateColumns(columns);
  if (columnErrors) {
    errors.push(...columnErrors);
  }

  // Validate filters
  const filterErrors = validateFilters(
    filters,
    selectedRows,
    options?.requireFiltersWhenNoSelection
  );
  if (filterErrors) {
    errors.push(...filterErrors);
  }

  // Validate row count if provided
  if (options?.estimatedRowCount !== undefined) {
    const rowErrors = validateRowCount(options.estimatedRowCount);
    if (rowErrors) {
      errors.push(...rowErrors);
    }
  }

  return errors.length > 0 ? errors : null;
}

/**
 * Check if validation errors contain hard failures (non-warnings)
 *
 * @param errors - Validation errors
 * @returns True if any hard failures exist
 */
export function hasHardFailures(
  errors: readonly ValidationError[] | null
): boolean {
  if (!errors) return false;

  return errors.some(error => {
    // Hard failures are errors on columns, filters, or row count exceeding limit
    if (error.field === 'columns' || error.field === 'filters') {
      return true;
    }

    if (
      error.field === 'rows' &&
      error.details?.rowCount &&
      typeof error.details.rowCount === 'number' &&
      error.details.rowCount > EXPORT_LIMITS.HARD_LIMIT
    ) {
      return true;
    }

    return false;
  });
}

/**
 * Format validation errors for display
 *
 * @param errors - Validation errors
 * @returns Formatted error messages
 */
export function formatValidationErrors(
  errors: readonly ValidationError[] | null
): readonly string[] {
  if (!errors) return [];

  return errors.map(error => error.message);
}

/**
 * Get first error message (for simple displays)
 *
 * @param errors - Validation errors
 * @returns First error message or null
 */
export function getFirstError(
  errors: readonly ValidationError[] | null
): string | null {
  if (!errors || errors.length === 0) return null;
  return errors[0].message;
}
